import { z } from 'zod';

// ── Swimlane ──
export const swimlaneSchema = z.object({
  id: z.string(),
  label: z.string(),
  role: z.string(),
});

// ── Guard definitions ──
export const guardSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('context'),
    path: z.string(),
    operator: z.enum(['>', '<', '>=', '<=', '==', '!=', 'includes', 'truthy', 'falsy']),
    value: z.any().optional(),
  }),
  z.object({
    type: z.literal('role'),
    allowedRoles: z.array(z.string()),
  }),
  z.object({
    type: z.literal('custom'),
    name: z.string(),
  }),
]);

// ── Action definitions ──
export const actionRefSchema = z.object({
  type: z.string(),
  params: z.record(z.any()).optional(),
});

// ── Transition ──
export const transitionSchema = z.object({
  target: z.string(),
  guard: z.string().optional(),
  actions: z.array(z.union([z.string(), actionRefSchema])).optional(),
});

// ── State meta — visual metadata for canvas rendering ──
export const stateMetaSchema = z.object({
  nodeType: z.enum(['task', 'gate', 'join', 'end', 'parallel', 'timer', 'subprocess']),
  label: z.string().optional(),
  swimlane: z.string().optional(),
  completionType: z.enum(['checkbox', 'checkbox_note', 'checkbox_linked']).optional(),
  estimatedMinutes: z.number().optional(),
  domain: z.string().optional(),
  taskId: z.string().optional(),
  options: z.array(z.string()).optional(), // gate paths
  duration: z.number().optional(), // timer: ms
  deadlineISO: z.string().optional(), // timer: absolute deadline
  linkedWorkflowId: z.string().optional(), // subprocess
}).passthrough();

// ── Machine state (recursive for nested/parallel) ──
const baseStateSchema = z.object({
  meta: stateMetaSchema.optional(),
  on: z.record(z.union([z.string(), transitionSchema, z.array(transitionSchema)])).optional(),
  after: z.record(z.union([z.string(), transitionSchema])).optional(),
  always: z.array(transitionSchema).optional(),
  invoke: z.object({
    src: z.string(),
    onDone: z.union([z.string(), transitionSchema]).optional(),
    onError: z.union([z.string(), transitionSchema]).optional(),
  }).optional(),
  type: z.enum(['parallel', 'final']).optional(),
  initial: z.string().optional(),
  onDone: z.union([z.string(), transitionSchema]).optional(),
}).passthrough();

// We use lazy recursion for nested states
export const machineStateSchema = baseStateSchema.extend({
  states: z.lazy(() => z.record(machineStateSchema)).optional(),
});

// ── Machine config ──
export const machineConfigSchema = z.object({
  id: z.string(),
  initial: z.string(),
  context: z.record(z.any()).optional(),
  states: z.record(machineStateSchema),
});

// ── Trigger definitions ──
export const triggerSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('cron'),
    expression: z.string(),
    action: z.string(),
  }),
  z.object({
    type: z.literal('sla'),
    stateId: z.string(),
    deadline: z.string(), // ISO 8601 duration (P2D, PT48H, etc.)
    escalateTo: z.string().optional(),
  }),
  z.object({
    type: z.literal('event'),
    eventName: z.string(),
    action: z.string(),
  }),
]);

// ── Full Workflow v2 Schema ──
export const workflowV2Schema = z.object({
  id: z.string(),
  name: z.string(),
  version: z.number().default(1),
  domain: z.string(),
  cadence: z.string(),
  regulatorySource: z.string().optional(),

  swimlanes: z.array(swimlaneSchema).default([]),

  machine: machineConfigSchema,

  triggers: z.array(triggerSchema).default([]),

  guards: z.record(guardSchema).default({}),

  actions: z.record(z.object({
    type: z.string(),
    channels: z.array(z.string()).optional(),
  }).passthrough()).default({}),
});

/**
 * Validate a workflow v2 JSON object.
 * Returns { success: true, data } or { success: false, error }.
 */
export function validateWorkflowV2(data) {
  return workflowV2Schema.safeParse(data);
}

/**
 * Extract flat nodes and edges from an XState machine config for ReactFlow rendering.
 * Walks the state tree recursively.
 */
export function machineToNodesEdges(machine, swimlanes = []) {
  const nodes = [];
  const edges = [];
  let edgeCounter = 0;

  function walkStates(states, parentId = null, depth = 0) {
    Object.entries(states).forEach(([stateId, stateConfig]) => {
      const meta = stateConfig.meta || {};
      const nodeType = meta.nodeType || (stateConfig.type === 'parallel' ? 'parallel' : stateConfig.type === 'final' ? 'end' : 'task');

      const node = {
        id: stateId,
        type: nodeType,
        position: { x: 0, y: 0 },
        label: meta.label || stateId,
        domain: meta.domain,
        completionType: meta.completionType,
        estimatedMinutes: meta.estimatedMinutes,
        options: meta.options,
        swimlane: meta.swimlane,
        taskId: meta.taskId,
        duration: meta.duration,
        linkedWorkflowId: meta.linkedWorkflowId,
        parentId: parentId,
        depth,
      };
      nodes.push(node);

      // Extract edges from `on` transitions
      if (stateConfig.on) {
        Object.entries(stateConfig.on).forEach(([eventName, target]) => {
          const transitions = Array.isArray(target) ? target : [target];
          transitions.forEach((t) => {
            const targetId = typeof t === 'string' ? t : t.target;
            if (targetId) {
              edges.push({
                id: `e-${edgeCounter++}`,
                from: stateId,
                to: targetId,
                label: eventName === 'COMPLETE' ? undefined : eventName,
                fromPort: eventName.toLowerCase(),
                guard: typeof t === 'object' ? t.guard : undefined,
              });
            }
          });
        });
      }

      // Extract edges from `always` transitions (guards/conditions)
      if (stateConfig.always) {
        stateConfig.always.forEach((t) => {
          if (t.target) {
            edges.push({
              id: `e-${edgeCounter++}`,
              from: stateId,
              to: t.target,
              label: t.guard || undefined,
              guard: t.guard,
            });
          }
        });
      }

      // Extract edges from `onDone` (parallel completion)
      if (stateConfig.onDone) {
        const target = typeof stateConfig.onDone === 'string' ? stateConfig.onDone : stateConfig.onDone.target;
        if (target) {
          edges.push({
            id: `e-${edgeCounter++}`,
            from: stateId,
            to: target,
            label: 'done',
          });
        }
      }

      // Extract edges from `invoke.onDone` (subprocess completion)
      if (stateConfig.invoke?.onDone) {
        const target = typeof stateConfig.invoke.onDone === 'string' ? stateConfig.invoke.onDone : stateConfig.invoke.onDone.target;
        if (target) {
          edges.push({
            id: `e-${edgeCounter++}`,
            from: stateId,
            to: target,
            label: 'subprocess done',
            isLoop: target === stateId || nodes.some((n) => n.id === target && edges.some((e) => e.from === target && e.to === stateId)),
          });
        }
      }

      // Recurse into nested states
      if (stateConfig.states) {
        walkStates(stateConfig.states, stateId, depth + 1);
      }
    });
  }

  walkStates(machine.states);
  return { nodes, edges };
}

/**
 * Convert flat nodes/edges back into XState machine config.
 * This is the reverse of machineToNodesEdges.
 */
export function nodesEdgesToMachine(nodes, edges, metadata = {}) {
  const states = {};

  // Build adjacency from edges
  const outgoing = {};
  edges.forEach((e) => {
    if (!outgoing[e.from]) outgoing[e.from] = [];
    outgoing[e.from].push(e);
  });

  // Find root nodes (no parent)
  const rootNodes = nodes.filter((n) => !n.parentId);

  rootNodes.forEach((node) => {
    const stateConfig = {};

    // Meta
    stateConfig.meta = {
      nodeType: node.type,
      label: node.label,
      ...(node.swimlane && { swimlane: node.swimlane }),
      ...(node.completionType && { completionType: node.completionType }),
      ...(node.estimatedMinutes && { estimatedMinutes: node.estimatedMinutes }),
      ...(node.domain && { domain: node.domain }),
      ...(node.taskId && { taskId: node.taskId }),
      ...(node.options && { options: node.options }),
      ...(node.duration && { duration: node.duration }),
      ...(node.linkedWorkflowId && { linkedWorkflowId: node.linkedWorkflowId }),
    };

    // Transitions from outgoing edges
    const nodeEdges = outgoing[node.id] || [];
    if (node.type === 'gate') {
      // Gate nodes use `always` with guards
      const always = nodeEdges.map((e) => ({
        target: e.to,
        ...(e.guard && { guard: e.guard }),
      }));
      if (always.length) stateConfig.always = always;
    } else if (node.type === 'end') {
      stateConfig.type = 'final';
    } else {
      // Regular nodes use `on` transitions
      const on = {};
      nodeEdges.forEach((e) => {
        const event = e.label || 'COMPLETE';
        const transition = { target: e.to };
        if (e.guard) transition.guard = e.guard;
        on[event] = transition;
      });
      if (Object.keys(on).length) stateConfig.on = on;
    }

    // Handle child nodes (parallel/subprocess)
    const children = nodes.filter((n) => n.parentId === node.id);
    if (children.length > 0) {
      stateConfig.states = {};
      children.forEach((child) => {
        stateConfig.states[child.id] = {
          meta: {
            nodeType: child.type,
            label: child.label,
            ...(child.swimlane && { swimlane: child.swimlane }),
            ...(child.completionType && { completionType: child.completionType }),
          },
          ...(child.type === 'end' ? { type: 'final' } : {}),
        };
        // Add transitions for children
        const childEdges = outgoing[child.id] || [];
        if (childEdges.length) {
          stateConfig.states[child.id].on = {};
          childEdges.forEach((e) => {
            stateConfig.states[child.id].on[e.label || 'COMPLETE'] = { target: e.to };
          });
        }
      });
      if (node.type === 'parallel') {
        stateConfig.type = 'parallel';
      }
    }

    states[node.id] = stateConfig;
  });

  // Determine initial state (first node in topological order, or first root node)
  const incoming = new Set(edges.map((e) => e.to));
  const startNodes = rootNodes.filter((n) => !incoming.has(n.id));
  const initial = startNodes[0]?.id || rootNodes[0]?.id || 'start';

  return {
    id: metadata.id || 'workflow',
    initial,
    context: metadata.context || {},
    states,
  };
}
